import { clamp, smoothstep } from '../core/math';
import { type AmbienceId, MusicEngine, type ThemeId } from './music';
import { isSfxId, SFX, type SfxDef, type SfxId } from './sfx';
import { dbToGain, generateImpulseResponse, softClipCurve, Synth } from './synth';
import { VoicePool } from './voicePool';

/**
 * Aetherfall's Web Audio engine.
 *
 * Graph: voices → bus (music / sfx / ui / ambient, each with a volume and a
 * reverb send) → master → glue compressor → limiter → soft clip → speakers.
 * A single convolution reverb (generated impulse response) is shared by all
 * buses. SFX are positioned relative to a listener on the ground plane
 * (stereo pan + distance attenuation + air-absorption lowpass + more reverb
 * when far). A voice pool caps concurrency and steals the oldest voice.
 *
 * The AudioContext is created lazily in `unlock()` (call it from a user
 * gesture, or use `attachUnlock()`); until then — and everywhere Web Audio is
 * missing (Node tests, headless) — every method is a safe no-op.
 */

export type BusId = 'music' | 'sfx' | 'ui' | 'ambient';
export type VolumeTarget = BusId | 'master';
export const BUS_IDS: readonly BusId[] = ['music', 'sfx', 'ui', 'ambient'];

export interface PlayOptions {
  /** World position on the ground plane. Omit for a non-positional sound. */
  x?: number;
  z?: number;
  /** Gain multiplier (default 1). */
  volume?: number;
  /** Pitch multiplier; lower pitches also play slower (bigger creatures). */
  pitch?: number;
  /** Start delay in seconds. */
  delay?: number;
  /** Recipe flavour: element for impacts, creature for growls, note for bells... */
  variant?: string;
  /** Duration hint in seconds for sounds that stretch (charge_up, telegraph_warning). */
  duration?: number;
  /** Route to another bus than the sound's default. */
  bus?: BusId;
}

export interface SpatialParams {
  /** Within this distance (m) sounds play at full volume. */
  refDistance: number;
  /** Sounds beyond this distance are culled. */
  maxDistance: number;
  rolloff: number;
  /** Lateral offset (m) at which the pan reaches half of `panMax`. */
  panWidth: number;
  panMax: number;
}

export const DEFAULT_SPATIAL: SpatialParams = {
  refDistance: 6,
  maxDistance: 55,
  rolloff: 1,
  panWidth: 8,
  panMax: 0.85,
};

export interface Listener {
  x: number;
  z: number;
  /** Camera yaw (CameraRig.yaw): screen-right is (cos yaw, -sin yaw) on the ground. */
  yaw: number;
}

export interface SpatialResult {
  pan: number;
  gain: number;
  dist: number;
  /** Air-absorption lowpass cutoff in Hz (0 = none). */
  lowpass: number;
  /** Reverb send multiplier (far sounds are wetter). */
  wet: number;
}

/** Pure positional model (inverse-distance rolloff, soft cull, screen-space pan). */
export function spatialize(
  l: Listener,
  x: number,
  z: number,
  p: SpatialParams = DEFAULT_SPATIAL,
): SpatialResult {
  const dx = x - l.x;
  const dz = z - l.z;
  const dist = Math.hypot(dx, dz);
  const right = dx * Math.cos(l.yaw) - dz * Math.sin(l.yaw);
  const pan = clamp(right / (Math.abs(right) + p.panWidth), -1, 1) * p.panMax;
  let gain =
    dist <= p.refDistance
      ? 1
      : p.refDistance / (p.refDistance + p.rolloff * (dist - p.refDistance));
  const fadeStart = p.maxDistance * 0.7;
  if (dist >= p.maxDistance) gain = 0;
  else if (dist > fadeStart) gain *= 1 - smoothstep(fadeStart, p.maxDistance, dist);
  const near = p.refDistance * 1.5;
  const lowpass = dist <= near ? 0 : clamp(20000 * (near / dist) ** 0.9, 1500, 20000);
  const wet = 1 + 0.6 * Math.min(1, dist / p.maxDistance);
  return { pan, gain, dist, lowpass, wet };
}

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  ui: number;
  ambient: number;
  muted: boolean;
}

export interface AudioEngineOptions {
  maxVoices?: number;
  /** Context factory (tests inject a fake). Return null when audio is unavailable. */
  createContext?: () => AudioContext | null;
  /** Run the internal 25 ms scheduler after unlock (default true). Tests call `pump()` by hand. */
  autoPump?: boolean;
  spatial?: Partial<SpatialParams>;
  /** Suspend the context while the tab is hidden (default true). */
  suspendWhenHidden?: boolean;
  rand?: () => number;
}

export interface MixBus {
  input: GainNode;
  vol: GainNode;
  wetIn: GainNode;
  wetVol: GainNode;
  /** Separate ducking stage (music only) so user volume and ducks never fight. */
  duck: GainNode | null;
  duckWet: GainNode | null;
}

export interface MixGraph {
  master: GainNode;
  reverbIn: GainNode;
  buses: Record<BusId, MixBus>;
}

interface ActiveVoice {
  id: SfxId;
  synth: Synth;
  /** Per-voice routing nodes (gain, air filter, panner, send). */
  head: AudioNode[];
  out: GainNode;
}

export const DEFAULT_VOLUMES: Readonly<Record<VolumeTarget, number>> = {
  master: 0.85,
  music: 0.7,
  sfx: 0.9,
  ui: 0.75,
  ambient: 0.6,
};

/** Slider value → gain (squared for a more even perceived loudness); master gets a little headroom trim. */
export const volumeGain = (target: VolumeTarget, value: number): number =>
  value * value * (target === 'master' ? 1.1 : 1);

/**
 * The mix: four buses (dry + reverb send each; music has a ducking stage) into
 * master → glue compressor → limiter → soft clip → destination, with one shared
 * band-limited convolution reverb. Exported so offline tools render through the
 * exact same chain. Bus/master gains start at 1; the engine applies volumes.
 */
export function createMixGraph(ctx: BaseAudioContext): MixGraph {
  const master = ctx.createGain();
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.knee.value = 12;
  comp.ratio.value = 3;
  comp.attack.value = 0.006;
  comp.release.value = 0.25;
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.1;
  const clip = ctx.createWaveShaper();
  clip.curve = softClipCurve();
  master.connect(comp);
  comp.connect(limiter);
  limiter.connect(clip);
  clip.connect(ctx.destination);

  // Shared reverb: band-limited send so sub thumps don't muddy the tail.
  const reverbIn = ctx.createGain();
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 180;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 7500;
  const conv = ctx.createConvolver();
  const [l, r] = generateImpulseResponse(ctx.sampleRate, {
    seconds: 2.6,
    decay: 3.2,
    damping: 0.7,
  });
  const ir = ctx.createBuffer(2, l.length, ctx.sampleRate);
  ir.getChannelData(0).set(l);
  ir.getChannelData(1).set(r);
  conv.buffer = ir;
  const reverbOut = ctx.createGain();
  reverbOut.gain.value = 0.8;
  reverbIn.connect(hp);
  hp.connect(lp);
  lp.connect(conv);
  conv.connect(reverbOut);
  reverbOut.connect(master);

  const mkBus = (duckable: boolean): MixBus => {
    const input = ctx.createGain();
    const vol = ctx.createGain();
    const wetIn = ctx.createGain();
    const wetVol = ctx.createGain();
    input.connect(vol);
    wetIn.connect(wetVol);
    let duck: GainNode | null = null;
    let duckWet: GainNode | null = null;
    if (duckable) {
      duck = ctx.createGain();
      duckWet = ctx.createGain();
      vol.connect(duck);
      duck.connect(master);
      wetVol.connect(duckWet);
      duckWet.connect(reverbIn);
    } else {
      vol.connect(master);
      wetVol.connect(reverbIn);
    }
    return { input, vol, wetIn, wetVol, duck, duckWet };
  };
  return {
    master,
    reverbIn,
    buses: { music: mkBus(true), sfx: mkBus(false), ui: mkBus(false), ambient: mkBus(false) },
  };
}

function defaultContext(): AudioContext | null {
  const g = globalThis as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor({ latencyHint: 'interactive' });
  } catch {
    return null;
  }
}

export function webAudioSupported(): boolean {
  const g = globalThis as { AudioContext?: unknown; webkitAudioContext?: unknown };
  return typeof g.AudioContext === 'function' || typeof g.webkitAudioContext === 'function';
}

export class AudioEngine {
  readonly pool: VoicePool;
  readonly spatial: SpatialParams;
  private ctx: AudioContext | null = null;
  private graph: MixGraph | null = null;
  private musicEngine: MusicEngine | null = null;
  private readonly voices = new Map<number, ActiveVoice>();
  private playedCount = 0;
  private dying: { voice: ActiveVoice; at: number }[] = [];
  private readonly listener: Listener = { x: 0, z: 0, yaw: 0 };
  private readonly volumes: Record<VolumeTarget, number> = { ...DEFAULT_VOLUMES };
  private readonly mutes: Record<VolumeTarget, boolean> = {
    master: false,
    music: false,
    sfx: false,
    ui: false,
    ambient: false,
  };
  private wantTheme: ThemeId | null = null;
  private wantCombat = false;
  private wantAmbience: AmbienceId | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly warned = new Set<string>();
  private readonly makeContext: () => AudioContext | null;
  private readonly autoPump: boolean;
  private readonly rand: () => number;
  private suspendWhenHidden: boolean;
  private hidden = false;
  private visibilityHooked = false;
  private failed = false;

  constructor(opts: AudioEngineOptions = {}) {
    this.pool = new VoicePool(opts.maxVoices ?? 40);
    this.spatial = { ...DEFAULT_SPATIAL, ...opts.spatial };
    this.makeContext = opts.createContext ?? defaultContext;
    this.autoPump = opts.autoPump ?? true;
    this.rand = opts.rand ?? Math.random;
    this.suspendWhenHidden = opts.suspendWhenHidden ?? true;
  }

  // ------------------------------------------------------------------ lifecycle

  /** True when this environment can produce sound (or already does). */
  get available(): boolean {
    return (
      this.ctx !== null ||
      (!this.failed && (this.makeContext !== defaultContext || webAudioSupported()))
    );
  }

  /** Context exists and is running. */
  get ready(): boolean {
    return this.ctx?.state === 'running';
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Current audio clock (0 before unlock). */
  get time(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /**
   * Creates (first call) and resumes the AudioContext. Call from a user
   * gesture handler. Returns false when Web Audio is unavailable.
   */
  unlock(): boolean {
    if (!this.ctx) {
      if (this.failed) return false;
      let ctx: AudioContext | null = null;
      try {
        ctx = this.makeContext();
      } catch (e) {
        this.warnOnce('create', `AudioContext unavailable: ${String(e)}`);
      }
      if (!ctx) {
        this.failed = true;
        return false;
      }
      this.ctx = ctx;
      try {
        this.build(ctx);
      } catch (e) {
        this.warnOnce('build', `Audio graph setup failed: ${String(e)}`);
        this.ctx = null;
        this.graph = null;
        this.failed = true;
        return false;
      }
      this.hookVisibility();
    }
    const ctx = this.ctx;
    if (
      ctx.state !== 'running' &&
      ctx.state !== 'closed' &&
      !(this.hidden && this.suspendWhenHidden)
    ) {
      void ctx.resume().catch(() => undefined);
      this.primeIOS(ctx);
    }
    if (this.autoPump && this.timer === null) this.timer = setInterval(() => this.pump(), 25);
    return true;
  }

  /**
   * Unlocks on the first pointer/key/touch gesture on `target` (window by
   * default). Returns a function that removes the listeners.
   */
  attachUnlock(
    target: EventTarget | null = typeof window !== 'undefined' ? window : null,
  ): () => void {
    if (!target) return () => undefined;
    const events = ['pointerdown', 'mousedown', 'keydown', 'touchend'];
    const off = (): void => {
      for (const e of events) target.removeEventListener(e, handler, true);
    };
    const handler = (): void => {
      if (!this.unlock() || this.ctx?.state === 'running') off();
    };
    for (const e of events) target.addEventListener(e, handler, true);
    return off;
  }

  /** Stops the scheduler, releases every voice and closes the context. */
  dispose(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.stopAll();
    this.musicEngine?.dispose();
    this.musicEngine = null;
    if (this.visibilityHooked && typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', this.onVisibility);
    this.visibilityHooked = false;
    const ctx = this.ctx;
    this.ctx = null;
    this.graph = null;
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined);
  }

  /** Scheduler tick: releases finished voices and schedules music. Runs every 25 ms after unlock. */
  pump(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const h of this.pool.expire(now)) {
      const v = this.voices.get(h);
      if (v) {
        this.voices.delete(h);
        this.disposeVoice(v);
      }
    }
    if (this.dying.length) {
      const keep: { voice: ActiveVoice; at: number }[] = [];
      for (const d of this.dying) {
        if (d.at <= now) this.disposeVoice(d.voice);
        else keep.push(d);
      }
      this.dying = keep;
    }
    if (ctx.state === 'running') this.musicEngine?.pump(now, this.hidden);
  }

  // ------------------------------------------------------------------ mixing

  /** Slider value 0..1 (a squared curve is applied to the gain). */
  setVolume(target: VolumeTarget, value: number): void {
    this.volumes[target] = clamp(Number.isFinite(value) ? value : 0, 0, 1);
    this.applyVolumes();
  }

  getVolume(target: VolumeTarget): number {
    return this.volumes[target];
  }

  setMuted(target: VolumeTarget, muted: boolean): void {
    this.mutes[target] = muted;
    this.applyVolumes();
  }

  isMuted(target: VolumeTarget): boolean {
    return this.mutes[target];
  }

  getSettings(): AudioSettings {
    const v = this.volumes;
    return {
      master: v.master,
      music: v.music,
      sfx: v.sfx,
      ui: v.ui,
      ambient: v.ambient,
      muted: this.mutes.master,
    };
  }

  applySettings(s: Partial<AudioSettings>): void {
    for (const k of ['master', 'music', 'sfx', 'ui', 'ambient'] as const) {
      const v = s[k];
      if (typeof v === 'number') this.volumes[k] = clamp(v, 0, 1);
    }
    if (typeof s.muted === 'boolean') this.mutes.master = s.muted;
    this.applyVolumes();
  }

  setSuspendWhenHidden(on: boolean): void {
    this.suspendWhenHidden = on;
  }

  /** Temporarily lowers the music (voice lines, ultimate impacts). */
  duck(db = -6, hold = 1.2, release = 0.9): void {
    const g = this.graph;
    const ctx = this.ctx;
    if (!g || !ctx) return;
    const now = ctx.currentTime;
    const k = dbToGain(Math.min(0, db));
    for (const node of [g.buses.music.duck, g.buses.music.duckWet]) {
      if (!node) continue;
      const p = node.gain;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.setTargetAtTime(k, now, 0.04);
      p.setTargetAtTime(1, now + Math.max(0.05, hold), Math.max(0.02, release / 3));
    }
  }

  // ------------------------------------------------------------------ sfx

  /** Listener position (the local hero) and camera yaw, once per frame. */
  setListener(x: number, z: number, yaw: number = this.listener.yaw): void {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    this.listener.x = x;
    this.listener.z = z;
    if (Number.isFinite(yaw)) this.listener.yaw = yaw;
  }

  getListener(): Readonly<Listener> {
    return this.listener;
  }

  /**
   * Plays a sound. Returns a voice handle, or null when nothing plays
   * (locked/unavailable, muted bus, culled by distance, voice limits).
   */
  play(id: SfxId, opts: PlayOptions = {}): number | null {
    const ctx = this.ctx;
    const graph = this.graph;
    if (!ctx || !graph || ctx.state !== 'running') return null;
    if (!isSfxId(id)) {
      this.warnOnce(`sfx:${String(id)}`, `Unknown sfx "${String(id)}"`);
      return null;
    }
    const def: SfxDef = SFX[id];
    const busId = opts.bus ?? def.bus ?? 'sfx';
    if (
      this.mutes.master ||
      this.mutes[busId] ||
      this.volumes[busId] <= 0 ||
      this.volumes.master <= 0
    )
      return null;
    let sp: SpatialResult | null = null;
    if (
      opts.x !== undefined &&
      opts.z !== undefined &&
      Number.isFinite(opts.x) &&
      Number.isFinite(opts.z)
    ) {
      sp = spatialize(this.listener, opts.x, opts.z, this.spatial);
      if (sp.gain <= 0.003) return null;
    }
    const volume = Math.max(0, opts.volume ?? 1) * (def.gain ?? 1) * (sp?.gain ?? 1);
    if (volume < 0.002) return null;

    const now = ctx.currentTime;
    const grant = this.pool.request({
      key: id,
      now,
      priority: def.priority ?? 1,
      maxInstances: def.maxInstances ?? 4,
      minInterval: def.minInterval ?? 0.03,
    });
    if (!grant) return null;
    for (const h of grant.stolen) this.killVoice(h, now);

    const r = this.rand;
    const userPitch = clamp(opts.pitch ?? 1, 0.25, 4);
    const pitch = userPitch * (1 + (r() * 2 - 1) * (def.pitchVar ?? 0.03));
    const stretch = clamp(1 / Math.sqrt(userPitch), 0.6, 1.8);
    const t0 = now + 0.004 + Math.max(0, opts.delay ?? 0) + r() * (def.jitter ?? 0.004);

    const bus = graph.buses[busId];
    const out = ctx.createGain();
    out.gain.value = volume;
    const head: AudioNode[] = [out];
    let input: AudioNode = out;
    if (sp && sp.lowpass > 0) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = sp.lowpass;
      lp.Q.value = 0.5;
      lp.connect(out);
      input = lp;
      head.push(lp);
    }
    if (sp && Math.abs(sp.pan) > 0.01 && typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = sp.pan;
      out.connect(p);
      p.connect(bus.input);
      head.push(p);
    } else {
      out.connect(bus.input);
    }
    const wet = (def.reverb ?? 0.1) * (sp?.wet ?? 1);
    if (wet > 0.005) {
      const w = ctx.createGain();
      w.gain.value = wet;
      out.connect(w);
      w.connect(bus.wetIn);
      head.push(w);
    }

    const synth = new Synth(ctx, input, t0, {
      pitch,
      stretch,
      rand: r,
      variant: opts.variant,
      hint: opts.duration,
    });
    try {
      def.build(synth);
    } catch (e) {
      this.pool.release(grant.handle);
      this.disposeVoice({ id, synth, head, out });
      this.warnOnce(`build:${id}`, `sfx "${id}" failed: ${String(e)}`);
      return null;
    }
    this.pool.setEnd(grant.handle, t0 + synth.end + 0.05);
    this.voices.set(grant.handle, { id, synth, head, out });
    this.playedCount++;
    return grant.handle;
  }

  /** Fades out one voice early. */
  stop(handle: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.pool.release(handle)) return;
    this.killVoice(handle, ctx.currentTime);
  }

  /** Fades out every sound effect (music keeps playing). */
  stopAll(): void {
    const now = this.ctx?.currentTime ?? 0;
    for (const h of this.pool.clear()) this.killVoice(h, now);
  }

  /** Active SFX voices. */
  get voiceCount(): number {
    return this.pool.size;
  }

  /** Sound effects started since the engine was created (diagnostics and tests). */
  get played(): number {
    return this.playedCount;
  }

  // ------------------------------------------------------------------ music facade

  /** Crossfades the score to a theme (null = silence). Remembered until unlock. */
  setTheme(id: ThemeId | null, fade?: number): void {
    this.wantTheme = id;
    this.musicEngine?.setTheme(id, fade);
  }

  get theme(): ThemeId | null {
    return this.wantTheme;
  }

  /** Adds (or removes) the combat percussion/ostinato layer. */
  setCombat(on: boolean): void {
    this.wantCombat = on;
    this.musicEngine?.setCombat(on);
  }

  get combat(): boolean {
    return this.wantCombat;
  }

  /** Overrides the ambience bed (null = follow the theme). */
  setAmbience(id: AmbienceId | null): void {
    this.wantAmbience = id;
    this.musicEngine?.setAmbience(id);
  }

  get music(): MusicEngine | null {
    return this.musicEngine;
  }

  // ------------------------------------------------------------------ internals

  private build(ctx: AudioContext): void {
    this.graph = createMixGraph(ctx);
    const buses = this.graph.buses;
    this.applyVolumes(true);

    const music = new MusicEngine({
      ctx,
      music: { dry: buses.music.input, wet: buses.music.wetIn },
      ambient: { dry: buses.ambient.input, wet: buses.ambient.wetIn },
    });
    this.musicEngine = music;
    music.setCombat(this.wantCombat);
    if (this.wantAmbience !== null) music.setAmbience(this.wantAmbience);
    if (this.wantTheme) music.setTheme(this.wantTheme, 2.5);
  }

  private applyVolumes(immediate = false): void {
    const g = this.graph;
    const ctx = this.ctx;
    if (!g || !ctx) return;
    const now = ctx.currentTime;
    const set = (p: AudioParam, v: number): void => {
      p.cancelScheduledValues(now);
      if (immediate) p.setValueAtTime(v, now);
      else {
        p.setValueAtTime(p.value, now);
        p.setTargetAtTime(v, now, 0.03);
      }
    };
    const curve = (t: VolumeTarget): number => (this.mutes[t] ? 0 : volumeGain(t, this.volumes[t]));
    set(g.master.gain, curve('master'));
    for (const id of BUS_IDS) {
      set(g.buses[id].vol.gain, curve(id));
      set(g.buses[id].wetVol.gain, curve(id));
    }
  }

  private killVoice(handle: number, now: number): void {
    const v = this.voices.get(handle);
    if (!v) return;
    this.voices.delete(handle);
    v.out.gain.setTargetAtTime(0, now, 0.012);
    for (const s of v.synth.sources) {
      try {
        s.stop(now + 0.08);
      } catch {
        // not started / already stopped
      }
    }
    this.dying.push({ voice: v, at: now + 0.12 });
  }

  private disposeVoice(v: ActiveVoice): void {
    for (const n of v.head) n.disconnect();
    for (const n of v.synth.nodes) n.disconnect();
  }

  /** Old iOS only unlocks audio when a buffer starts inside the gesture. */
  private primeIOS(ctx: AudioContext): void {
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      src.connect(ctx.destination);
      src.start(0);
      src.stop(ctx.currentTime + 0.01);
    } catch {
      // harmless
    }
  }

  private readonly onVisibility = (): void => {
    if (typeof document === 'undefined') return;
    this.hidden = document.hidden;
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return;
    if (this.hidden && this.suspendWhenHidden) void ctx.suspend().catch(() => undefined);
    else if (!this.hidden) void ctx.resume().catch(() => undefined);
  };

  private hookVisibility(): void {
    if (
      this.visibilityHooked ||
      typeof document === 'undefined' ||
      typeof document.addEventListener !== 'function'
    )
      return;
    this.visibilityHooked = true;
    this.hidden = !!document.hidden;
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private warnOnce(key: string, msg: string): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    console.warn(`[audio] ${msg}`);
  }
}
